// This is a COPY of downread/shared/src/utils/feed-cdn.ts. To modify, edit the original and then run downread/bin/build-shared.sh
// Which CDN a feed's content (post files) and images live on — a pure function of the feed id.
//
// Feed content + images are served from `<cdnBase>/<feedId>/…`. Only EU private/newsletter feeds
// (id prefix 'fpe'/'fpn') are stored on the EU CDN; ALL other feeds — every public feed, plus US
// private/newsletter ('fpp'/'fpm') — live on the US CDN. So the CDN is determined by the id prefix
// alone, NEVER by the viewer's jurisdiction: a US user reading an EU user's shared private feed
// still fetches it from the EU CDN, and vice-versa. (Discover/shared/random content is global and
// always on the US CDN — that's handled separately via getCdnUrl, not here.)

// Feed-id prefixes. Public feeds are 'f' + 2-char instance + random and never start with 'fp'. The
// 'fp' namespace is reserved for private-like feeds, the 3rd char encoding type + jurisdiction:
//   'fpp' = PRIVATE (US)     'fpe' = PRIVATE (EU)
//   'fpm' = NEWSLETTER (US)  'fpn' = NEWSLETTER (EU)
export const FEED_PREFIX_PRIVATE_US = 'fpp';
export const FEED_PREFIX_PRIVATE_EU = 'fpe';
export const FEED_PREFIX_NEWSLETTER_US = 'fpm';
export const FEED_PREFIX_NEWSLETTER_EU = 'fpn';

/** True iff a feed id falls in the namespace reserved for private/newsletter feeds — no public feed
 *  may use it. Every public feed-id generator must reject ids for which this returns true.
 *
 *  Two rules, so that already-existing (buggy) public 'fp…' feeds don't have to be renamed:
 *   1. The four live private/newsletter prefixes (fpp/fpe/fpm/fpn) are ALWAYS reserved — real
 *      private/newsletter ids are far longer than 6 chars, so no legitimate public feed has one.
 *   2. Any other 'fp…' id is reserved only when it is 6 chars or shorter. Longer 'fp…' ids are the
 *      historical public feeds minted before the reservation existed, so they stay valid. */
export const isReservedFeedId = (feedId: string): boolean =>
    feedId.startsWith(FEED_PREFIX_PRIVATE_US) || feedId.startsWith(FEED_PREFIX_PRIVATE_EU) ||
    feedId.startsWith(FEED_PREFIX_NEWSLETTER_US) || feedId.startsWith(FEED_PREFIX_NEWSLETTER_EU) ||
    (feedId.startsWith('fp') && feedId.length <= 6);

export const US_CDN_BASE_URL = 'https://data.downread.com/';
export const EU_CDN_BASE_URL = 'https://data-eu.downread.com/';

/** True iff this feed's content/images live on the EU CDN (EU private 'fpe' or EU newsletter 'fpn'). */
export const isEuCdnFeedId = (feedId: string): boolean =>
    feedId.startsWith(FEED_PREFIX_PRIVATE_EU) || feedId.startsWith(FEED_PREFIX_NEWSLETTER_EU);

/** True iff this is a NEWSLETTER feed id (US 'fpm' or EU 'fpn'). A cheap pre-check that avoids a feed
 *  read on ordinary unsubscribes; callers still confirm feedStatus == 'NEWSLETTER'. */
export const isNewsletterFeedId = (feedId: string): boolean =>
    feedId.startsWith(FEED_PREFIX_NEWSLETTER_US) || feedId.startsWith(FEED_PREFIX_NEWSLETTER_EU);

/** True iff this is a PRIVATE or NEWSLETTER feed id (fpp/fpe/fpm/fpn) — the feeds whose post-storage
 *  cap follows their subscribers' plans. Prefix check only; callers still confirm feedStatus. */
export const isPrivateLikeFeedId = (feedId: string): boolean =>
    feedId.startsWith(FEED_PREFIX_PRIVATE_US) || feedId.startsWith(FEED_PREFIX_PRIVATE_EU) || isNewsletterFeedId(feedId);

/** CDN base URL (with trailing slash) a feed's content + images are served from, by id prefix alone. */
export const feedCdnBaseUrl = (feedId: string): string =>
    isEuCdnFeedId(feedId) ? EU_CDN_BASE_URL : US_CDN_BASE_URL;

/** Which CDN holds a feed's current.dz. `null` (feeds.cdn) means it is not uploaded at all and can
 *  only be read from the sync instance that stored it (feeds.syncInstance); the feed's archive
 *  files still live on the CDN this function names. */
export type FeedCdn = 'us' | 'eu';

/** The CDN a feed's files go to when CDN storage is on — the id-prefix rule above, as a stored value. */
export const feedCdnForId = (feedId: string): FeedCdn => isEuCdnFeedId(feedId) ? 'eu' : 'us';

/** Base URL (with trailing slash) for a stored FeedCdn value. */
export const cdnBaseUrl = (cdn: FeedCdn): string => cdn == 'eu' ? EU_CDN_BASE_URL : US_CDN_BASE_URL;
